import { LABELS } from '@/constants/common/message.constants'

import { type DispatchDetailFrameType } from '../dispatches/dispatch-detail-frame-type'

// ドライバー・配送種別で共有するカラー選択肢を定義
export const COLOR_TYPE = [
  '#FA541C',
  '#FF7A45',
  '#FA8C16',
  '#FAAD14',
  '#AD6800',
  '#5B8C00',
  '#A0D911',
  '#52C41A',
  '#08979C',
  '#13C2C2',
  '#722ED1',
  '#391085',
  '#1890FF',
  '#0050B3',
  '#9E1068',
  '#EB2F96',
  '#FF85C0',
  '#AD8B00',
  '#FF4D4F',
  '#A8071A'
] as const

// VOC_PJ-932: ドライバーのみ非設定ニーズに対応するため白を追加（配送種別には適用しない）
export const DRIVER_COLOR_TYPE = ['#FFFFFF', ...COLOR_TYPE] as const

// 予定枠・空き枠の背景色パレット（VOC_PJ-450 #3830）。
// Issue 本文が定める 21 色（White + 極淡色 20 色）のうち、末尾の灰（#EDEDED、墨）を除いた 20 色。
// 極淡色は Material Design の 500 番色に白を 92% 混ぜたもの（#3830 の MTG 合意）。
// COLOR_TYPE / DRIVER_COLOR_TYPE への合成はしない（#4677 で COLOR_TYPE への
// 追加が配送種別へ波及した経緯があるため、独立した配列にする）。
const FRAME_COLOR_VALUES = [
  '#FFFFFF',
  '#FEEFEE',
  '#FDEDF2',
  '#F7EDF8',
  '#F2EFF9',
  '#EFF1F9',
  '#EDF6FE',
  '#EAF8FE',
  '#EAF9FB',
  '#EAF6F5',
  '#F0F8F1',
  '#F5FAF0',
  '#FBFCEF',
  '#FFFDEF',
  '#FFFAEB',
  '#FFF6EA',
  '#FFF1ED',
  '#F4F1F0',
  '#F7F7F7',
  '#F2F4F5'
] as const

// 枠の背景色の濃いめ版。淡色 20 色の元になった Material Design 500 番色に白を 80% 混ぜたもので、
// FRAME_COLOR_VALUES と同じ色相の並びで置く。#3830 より前の種類固定色（空き時間枠 #FEFDCE、
// 予定枠 #E2E8F0）に近い濃さを狙っている（黄 #FFFBD8 / 青灰 #DFE5E8）。
// 白は両方で同じ値になるため、淡色側にだけ置く。末尾の墨は濃いめ側にだけ置く。
// 淡い色だけでは枠が目立たず見分けにくいという要望に応えて、同じ色相をどちらの濃さでも選べるようにした。
const FRAME_DEEP_COLOR_VALUES = [
  '#FDD9D7',
  '#FBD2E0',
  '#EBD4EF',
  '#E1D8F1',
  '#D9DCF0',
  '#D3EAFD',
  '#CDEEFD',
  '#CCF2F6',
  '#CCEAE7',
  '#DBEFDC',
  '#E8F3DB',
  '#F5F8D7',
  '#FFFBD8',
  '#FFF3CD',
  '#FFEACC',
  '#FFDDD3',
  '#E4DDDA',
  '#ECECEC',
  '#DFE5E8',
  '#D3D3D3'
] as const

export type FrameColorTone = 'LIGHT' | 'DEEP'

export type FrameColorOption = {
  value: string
  /** 画面に表示せず、選択肢の読み上げ用（aria-label）にのみ使う色名 */
  name: string
  tone: FrameColorTone
}

// 日本語の色名は message.constants.ts の FRAME_COLOR_NAMES（FRAME_COLOR_VALUES と同じ並び順、先頭が白）に
// 定数化してある。濃いめは白を持たないため、色名は 2 番目（赤）から対応させる（末尾の墨は濃いめだけが持つ）
const toFrameColorOptions = (
  values: readonly string[],
  tone: FrameColorTone,
  nameOffset: number
): FrameColorOption[] =>
  values.map((value, index) => ({
    value,
    name: LABELS.FRAME_COLOR_NAMES[index + nameOffset],
    tone
  }))

export const FRAME_LIGHT_COLOR_TYPE = toFrameColorOptions(
  FRAME_COLOR_VALUES,
  'LIGHT',
  0
)
export const FRAME_DEEP_COLOR_TYPE = toFrameColorOptions(
  FRAME_DEEP_COLOR_VALUES,
  'DEEP',
  1
)

// 色選択（ColorPicker の presets）の保存済みパレット。濃さ（淡い / 濃い）ごとにグループ分けして表示する
export const FRAME_COLOR_PALETTES: readonly {
  tone: FrameColorTone
  label: string
  colors: FrameColorOption[]
}[] = [
  {
    tone: 'LIGHT',
    label: LABELS.FRAME_COLOR_TONE_LIGHT,
    colors: FRAME_LIGHT_COLOR_TYPE
  },
  {
    tone: 'DEEP',
    label: LABELS.FRAME_COLOR_TONE_DEEP,
    colors: FRAME_DEEP_COLOR_TYPE
  }
]

// パレットの全量（淡色 20 色 + 濃いめ 20 色）。HEX から色名を引くなど、グループを問わない参照に使う。
// ColorPicker では自由な色も選べるため、保存される色がこの全量に含まれるとは限らない
export const FRAME_COLOR_TYPE: FrameColorOption[] = [
  ...FRAME_LIGHT_COLOR_TYPE,
  ...FRAME_DEEP_COLOR_TYPE
]

// 枠を作るときの既定の背景色（VOC_PJ-1056）。#3830 で種類固定色を枠ごとの色へ置き換えた際に
// 既定が白になったが、種類が一目で分かるよう、旧の種類固定色（空き時間枠 #fefdce の黄、
// 予定枠 #e2e8f0 の青灰）に近いパレット色を既定にする。淡色では枠が目立たないため濃いめ側の
// 色を使う。パレット外の色にすると色選択欄がどの選択肢も選んでいない表示になるため、パレットから選ぶ。
// 配送の矢羽根は背景色を持たない（列は互換のため残り、値は白）。
// BE の DEFAULT_FRAME_COLOR_BY_KIND（dispatches/board-write/dispatch-frame-writer.ts）と同じ値にする。
export const DEFAULT_FRAME_COLOR_BY_TYPE = {
  FREE_TIME: '#FFFBD8',
  SCHEDULED: '#DFE5E8',
  DELIVERY: '#FFFFFF'
} as const satisfies Record<
  DispatchDetailFrameType,
  (typeof FRAME_COLOR_VALUES)[number] | (typeof FRAME_DEEP_COLOR_VALUES)[number]
>
